import React, { useEffect, useState } from "react";
import { Clock, Settings, AlertTriangle, Calendar } from "lucide-react";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";
const card = "rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022]";
const inputCls =
  "w-full bg-black border border-[#00ff90]/20 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[#00ff90]/50 transition-colors";
const labelCls = "text-xs text-[#00ff90]/50 uppercase tracking-wider block mb-1.5";

const DAY_OPTIONS = [
  { v: 1, label: "Mon" },
  { v: 2, label: "Tue" },
  { v: 3, label: "Wed" },
  { v: 4, label: "Thu" },
  { v: 5, label: "Fri" },
  { v: 6, label: "Sat" },
  { v: 7, label: "Sun" },
];

const DEFAULTS = {
  work_start: "09:00",
  work_end: "17:30",
  working_days: [1, 2, 3, 4, 5],
  late_threshold_mins: 30,
  grace_period_mins: 0,
  overtime_enabled: false,
  overtime_threshold_hours: 8.0,
};

export default function AttendanceSettingsPage() {
  const [form, setForm] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const token = localStorage.getItem("token");

  useEffect(() => {
    fetch(`${API_BASE}/api/admin/attendance/settings`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((d) => {
        if (d && typeof d === "object") {
          setForm((prev) => ({ ...prev, ...d }));
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleDay = (v) =>
    set("working_days",
      form.working_days.includes(v)
        ? form.working_days.filter((d) => d !== v)
        : [...form.working_days, v].sort((a, b) => a - b)
    );

  const save = async () => {
    setSaving(true); setMsg(null);
    try {
      const res = await fetch(`${API_BASE}/api/admin/attendance/settings`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.detail || `HTTP ${res.status}`);
      }
      setMsg({ type: "success", text: "Settings saved." });
    } catch (e) {
      setMsg({ type: "error", text: typeof e.message === "string" ? e.message : String(e) });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className={`${card} p-5`}><div className="h-48 rounded-lg bg-white/5 animate-pulse" /></div>;
  }

  return (
    <div className="space-y-5">
      <div className={card}>
        <div className="px-5 py-4 border-b border-white/5 flex items-center gap-2">
          <Clock size={13} className="text-[#00ff90]/60" />
          <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">Work Hours</span>
        </div>
        <div className="p-5 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className={labelCls}>Start time</label>
            <input className={inputCls} type="time" value={form.work_start}
              onChange={(e) => set("work_start", e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>End time</label>
            <input className={inputCls} type="time" value={form.work_end}
              onChange={(e) => set("work_end", e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>Working days</label>
            <div className="flex flex-wrap gap-1.5 mt-1.5">
              {DAY_OPTIONS.map((d) => {
                const on = form.working_days.includes(d.v);
                return (
                  <button key={d.v} type="button" onClick={() => toggleDay(d.v)}
                    className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${
                      on
                        ? "bg-[#00ff90]/15 border-[#00ff90]/40 text-[#00ff90]"
                        : "bg-white/5 border-white/10 text-white/40"
                    }`}>
                    {d.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <div className={card}>
        <div className="px-5 py-4 border-b border-white/5 flex items-center gap-2">
          <AlertTriangle size={13} className="text-[#00ff90]/60" />
          <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">Late Arrival</span>
        </div>
        <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Late threshold (mins past start)</label>
            <input className={inputCls} type="number" min="0" max="240"
              value={form.late_threshold_mins}
              onChange={(e) => set("late_threshold_mins", Number(e.target.value))} />
            <p className="text-xs text-white/25 mt-1">Check-ins after start time + this delta count as Late.</p>
          </div>
          <div>
            <label className={labelCls}>Grace period (mins)</label>
            <input className={inputCls} type="number" min="0" max="60"
              value={form.grace_period_mins}
              onChange={(e) => set("grace_period_mins", Number(e.target.value))} />
            <p className="text-xs text-white/25 mt-1">Forgiven within this extra window before Late triggers.</p>
          </div>
        </div>
      </div>

      <div className={card}>
        <div className="px-5 py-4 border-b border-white/5 flex items-center gap-2">
          <Calendar size={13} className="text-[#00ff90]/60" />
          <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">Overtime</span>
        </div>
        <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={form.overtime_enabled}
              onChange={(e) => set("overtime_enabled", e.target.checked)}
              className="w-4 h-4 rounded accent-[#00ff90]" />
            <span className="text-sm text-white/70">Track overtime</span>
          </label>
          <div>
            <label className={labelCls}>Daily threshold (hours)</label>
            <input className={inputCls} type="number" min="0" step="0.5"
              value={form.overtime_threshold_hours}
              onChange={(e) => set("overtime_threshold_hours", Number(e.target.value))}
              disabled={!form.overtime_enabled} />
          </div>
        </div>
      </div>

      {msg && (
        <div className={`rounded-xl border px-4 py-3 text-sm ${
          msg.type === "error"
            ? "border-red-500/30 bg-red-500/10 text-red-300"
            : "border-[#00ff90]/30 bg-[#00ff90]/10 text-[#00ff90]"
        }`}>{msg.text}</div>
      )}

      <div className="flex justify-end">
        <button onClick={save} disabled={saving}
          className="text-xs px-6 py-2 rounded-lg bg-[#00ff90]/15 text-[#00ff90] border border-[#00ff90]/30 hover:bg-[#00ff90]/25 disabled:opacity-40 transition-colors font-medium">
          {saving ? "Saving…" : "Save Attendance Settings"}
        </button>
      </div>
    </div>
  );
}
